<template>
  <section>
    <header>{{ title }}</header>

    <VSwitch
      theme="slider"
      checked-label="Есть предоплата"
      unchecked-label="Нет предоплаты"
      v-model="prepaymentSwitch"
      :disabled="prepaymentTermDisabled"
    />

    <!--------------------	Prepayment -------------------->
    <template v-if="hasPrepaymentTerm">
      <VInputNumber
        ref="prepaymentTermPercentageInput"
        key="prepaymentTermPercentage"
        label="Процент предоплаты"
        layout="vertical"
        placeholder="Введите процент предоплаты"
        :precision="0"
        required
        :min="0"
        :validate-method="validatePrepaymentPercentage"
        v-model="prepaymentTerm.percentage"
        :disabled="prepaymentTermDisabled"
        @update:modelValue="onPrepaymentInput('percentage', $event)"
      />

      <VFormItem>
        <VSelect
          ref="prepaymentTermEventInput"
          key="prepaymentTermEvent"
          label="Когда оплатить"
          layout="vertical"
          placeholder="Выберите событие после которого оплатить"
          :options="prePaymentEventList"
          :clearable="false"
          required
          v-model="prepaymentTerm.event"
          :disabled="prepaymentTermDisabled"
          @update:modelValue="onPrepaymentInput('event', $event)"
        />

        <VInputNumber
          ref="prepaymentTermDaysInput"
          key="prepaymentTermDays"
          label="Дней"
          layout="vertical"
          :precision="0"
          :min="1"
          required
          v-model="prepaymentTerm.days"
          :disabled="prepaymentTermDisabled"
          @update:modelValue="onPrepaymentInput('days', $event)"
        />
      </VFormItem>
    </template>

    <VSwitch
      ref="postPaymentTermInput"
      theme="slider"
      checked-label="Есть постоплата"
      unchecked-label="Нет постоплаты"
      :validate-method="validatePaymentTerm"
      v-model="postpaymentSwitch"
      :disabled="postpaymentTermDisabled"
    />

    <!--------------------	Postpayment -------------------->
    <template v-if="hasPostpaymentTerm">
      <VInputNumber
        ref="postpaymentTermPercentageInput"
        key="postpaymentTermPercentage"
        label="Процент постоплаты"
        layout="vertical"
        placeholder="Введите процент постоплаты"
        :precision="0"
        required
        :min="0"
        :validate-method="validatePostpaymentPercentage"
        v-model="postpaymentTerm.percentage"
        :disabled="postpaymentTermDisabled"
        @update:modelValue="onPostpaymentInput('percentage', $event)"
      />

      <VFormItem>
        <VSelect
          ref="postpaymentTermEventInput"
          key="postpaymentTermEvent"
          label="Когда оплатить"
          layout="vertical"
          placeholder="Выберите событие после которого оплатить"
          :options="postPaymentEventList"
          :clearable="false"
          required
          v-model="postpaymentTerm.event"
          :disabled="postpaymentTermDisabled"
          @update:modelValue="onPostpaymentInput('event', $event)"
        />

        <VInputNumber
          ref="postpaymentTermDaysInput"
          key="postpaymentTermDays"
          label="Дней"
          layout="vertical"
          :precision="0"
          :min="1"
          required
          v-model="postpaymentTerm.days"
          :disabled="postpaymentTermDisabled"
          @update:modelValue="onPostpaymentInput('days', $event)"
        />
      </VFormItem>
    </template>
  </section>
</template>

<script lang="ts">

import PaymentTerm         from '@/domains/PaymentTerms/PaymentTerm';
import PaymentTermEvent    from '@/domains/PaymentTerms/PaymentTermEvent';
import PaymentTermsService from '@/domains/PaymentTerms/PaymentTermsService';

export interface HasPaymentTerms {
  paymentTerms: PaymentTerm[];
}

export default defineComponent({
  name: 'PaymentTermEditor',

  props: {
    entity: {
      type:     Object as PropType<HasPaymentTerms>,
      required: true,
    },
    title: {
      type:    String,
      default: 'График оплаты',
    },
    disabled: {
      type:    Boolean,
      default: false,
    },
    /** Когда можно выбрать только один вариант оплаты */
    singleMode: {
      type:    Boolean,
      default: false,
    },
    /** Если на основе предложения, то варианта оплаты after_order_confirm быть не может */
    isOfferBased: {
      type:    Boolean,
      default: false,
    },
  },

  computed: {
    prepaymentTermDisabled(): boolean {
      return this.disabled === true
        || (this.singleMode === true && this.hasPostpaymentTerm);
    },

    postpaymentTermDisabled(): boolean {
      return this.disabled === true
        || (this.singleMode === true && this.hasPrepaymentTerm);
    },

    /** Пред-терм из общего массива (по типу события), либо null */
    prepaymentTerm(): PaymentTerm | null {
      const term = this.entity.paymentTerms?.find(
        (item: PaymentTerm) => item.event?.isPrepayment === true,
      );
      return term ?? null;
    },

    /** Пост-терм из общего массива (по типу события), либо null */
    postpaymentTerm(): PaymentTerm | null {
      const term = this.entity.paymentTerms?.find(
        (item: PaymentTerm) => item.event?.isPostpayment === true,
      );
      return term ?? null;
    },

    hasPrepaymentTerm(): boolean {
      return this.prepaymentTerm !== null;
    },

    hasPostpaymentTerm(): boolean {
      return this.postpaymentTerm !== null;
    },

    prePaymentEventList(): PaymentTermEvent[] {
      return PaymentTermsService.getEventsByPaymentType('pre');
    },

    postPaymentEventList(): PaymentTermEvent[] {
      const events = PaymentTermsService.getEventsByPaymentType('post');

      if (this.isOfferBased) {
        return events.filter((event: PaymentTermEvent) => event.id !== 'after_order_confirm');
      }

      return events;
    },

    prepaymentSwitch: {
      get(): boolean {
        return this.hasPrepaymentTerm;
      },
      set(value: boolean) {
        this.onPrepaymentTermToggle(value);
      },
    },

    postpaymentSwitch: {
      get(): boolean {
        return this.hasPostpaymentTerm;
      },
      set(value: boolean) {
        this.onPostpaymentTermToggle(value);
      },
    },
  },

  methods: {
    onPrepaymentTermToggle(value: boolean): void {
      if (value) {
        // Пустой терм нельзя классифицировать без event —
        // задаём событие по умолчанию из pre-списка
        const term = new PaymentTerm();
        term.event = this.prePaymentEventList[0] ?? null;
        this.entity.paymentTerms.push(term);
      } else {
        this.entity.paymentTerms = this.entity.paymentTerms.filter(
          (item: PaymentTerm) => item.event?.isPrepayment !== true,
        );
      }

      (this.$refs.postPaymentTermInput as any).validate();
    },

    onPostpaymentTermToggle(value: boolean): void {
      if (value) {
        // Пустой терм нельзя классифицировать без event —
        // задаём событие по умолчанию из post-списка
        const term = new PaymentTerm();
        term.event = this.postPaymentEventList[0] ?? null;
        this.entity.paymentTerms.push(term);
      } else {
        this.entity.paymentTerms = this.entity.paymentTerms.filter(
          (item: PaymentTerm) => item.event?.isPostpayment !== true,
        );
      }

      (this.$refs.postPaymentTermInput as any).validate();
    },

    onPrepaymentInput(field: string, value: any): void {
      const term = this.prepaymentTerm;
      if (!term) return;

      term[field] = value;

      if (field === 'percentage') {
        if (this.hasPostpaymentTerm && this.$core.utils.number.isNumber(this.postpaymentTerm.percentage)) {
          (this.$refs.postpaymentTermPercentageInput as any).validate();
        }
      }
    },

    onPostpaymentInput(field: string, value: any): void {
      const term = this.postpaymentTerm;
      if (!term) return;

      term[field] = value;

      if (field === 'percentage') {
        if (this.hasPrepaymentTerm && this.$core.utils.number.isNumber(this.prepaymentTerm.percentage)) {
          (this.$refs.prepaymentTermPercentageInput as any).validate();
        }
      }
    },

    validatePostpaymentPercentage(value: number): string | null {
      if (!this.$core.utils.number.isNumber(value)) return null;

      if (
        this.hasPrepaymentTerm
        && this.$core.utils.number.isNumber(this.prepaymentTerm.percentage)
      ) {
        return value + this.prepaymentTerm.percentage !== 100
          ? 'Сумма пред- и постоплат должна быть равна 100%'
          : null;
      }
      return value !== 100 ? 'Должна быть равна 100%' : null;
    },

    validatePrepaymentPercentage(value: number): string | null {
      if (!this.$core.utils.number.isNumber(value)) return null;

      if (
        this.hasPostpaymentTerm
        && this.$core.utils.number.isNumber(this.postpaymentTerm.percentage)
      ) {
        return value + this.postpaymentTerm.percentage !== 100
          ? 'Сумма пред- и постоплат должна быть равна 100%'
          : null;
      }
      return value !== 100 ? 'Должна быть равна 100%' : null;
    },

    validatePaymentTerm(): string | null {
      return this.hasPrepaymentTerm || this.hasPostpaymentTerm
        ? null
        : 'Необходимо указать пред- или постоплату';
    },
  },
});
</script>
